import { OidcClient, UserManager, UserManagerSettings } from 'oidc-client-ts';
import type { AuthProviderProps } from 'react-oidc-context';
import { Capacitor } from '@capacitor/core';
import { NATIVE_APP_SCHEME, XIMI_SSO_AUTHORITY, XIMI_SSO_CLIENT_ID } from '../../config';
import { XIMI_SSO_CALLBACK_PATH } from '../../routePaths';
import { PreferencesStateStore } from './oidcStateStore';

const isNative = Capacitor.isNativePlatform();

/**
 * Native redirect, back from the system browser (RFC 8252): captured by
 * ASWebAuthenticationSession on iOS, delivered as a deeplink (appUrlOpen) on Android.
 * Registered as an allowed redirect URI on both Stimul'In clients (QA, prod).
 */
export const XIMI_NATIVE_REDIRECT_URI = `${NATIVE_APP_SCHEME}://ximi/sso`;

/** Native post-logout redirect (Android end_session in a Custom Tab), registered on both clients. */
export const XIMI_NATIVE_POST_LOGOUT_URI = `${NATIVE_APP_SCHEME}://ximi/logout`;

// Web → real origin route ; native → app scheme (above).
const redirectUri = isNative
    ? XIMI_NATIVE_REDIRECT_URI
    : `${window.location.origin}${XIMI_SSO_CALLBACK_PATH}`;

// Single storage backend (preferences wrapper) for user + signin state, shared by the
// UserManager and the OidcClient below, so the PKCE verifier created on native is found
// at callback. Swap to native secure storage later = one change in the wrapper.
const store = new PreferencesStateStore();

const settings: UserManagerSettings = {
    authority: XIMI_SSO_AUTHORITY,
    client_id: XIMI_SSO_CLIENT_ID,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: window.location.origin, // web only: native logout = revocation
    response_type: 'code',
    scope: 'openid offline_access email roles profile',
    automaticSilentRenew: true,
    loadUserInfo: true, // appelle /userinfo après /token → claims profile (name…) dans user.profile
    userStore: store,
    stateStore: store
};

/** Drives react-oidc-context (web auto-callback) AND the native system-browser callback. */
export const ximiUserManager = new UserManager(settings);

/** Native only: builds the /authorize request URL (stores the PKCE verifier in `store`). */
export const ximiOidcClient = new OidcClient(settings);

export const ximiOidcConfig: AuthProviderProps = {
    userManager: ximiUserManager,
    onSigninCallback: () => {
        window.history.replaceState({}, document.title, window.location.pathname);
    }
};
